// Compact context block for the Conversation right rail. Reads the
// visitor's most recent presence payload — browser, OS, viewport, language,
// timezone, geo, origin/page — and renders it as labeled lines.
//
// Best-effort: every field is optional. The visitor's widget collects them
// via `navigator.*` / `Intl.*` and ships them with `visitor:presence`. We
// parse the UA once with a tiny inline matcher (no external dep).

import { useMemo } from 'react';
import type { VisitorId } from '@prakash-software/shared';
import { useVisitorsStore } from '../stores/visitors';
import { LocalClock } from './LocalClock';
import styles from './DeviceContext.module.css';

export function DeviceContext({ visitorId }: { visitorId: VisitorId }) {
  const row = useVisitorsStore((s) => s.rows.get(visitorId));
  const parsed = useMemo(() => parseUA(row?.context?.userAgent), [row?.context?.userAgent]);

  if (!row) return null;
  const ctx = row.context;
  const geo = row.identity.geo;

  const haveAny =
    ctx?.userAgent ||
    ctx?.viewport ||
    ctx?.language ||
    ctx?.timezone ||
    ctx?.origin ||
    ctx?.pagePath ||
    geo?.country ||
    geo?.city ||
    geo?.ip;

  if (!haveAny) return null;

  const location = [geo?.city, geo?.region, geo?.country].filter(Boolean).join(', ');

  return (
    <section className={styles.root} aria-labelledby="ctx-heading">
      {/* Before the field list, not inside it: "is it the middle of their
          night?" changes whether an agent reaches out at all, so it outranks
          the browser version it would otherwise be filed beside. */}
      <LocalClock
        timezone={ctx?.timezone}
        latitude={geo?.latitude}
        longitude={geo?.longitude}
        place={location || null}
      />
      <dl className={styles.list} id="ctx-heading">
        {parsed.browser ? <Row label="Browser" value={parsed.browser} /> : null}
        {parsed.os ? <Row label="Device" value={parsed.os} /> : null}
        {ctx?.viewport ? (
          <Row label="Screen" value={`${ctx.viewport.width} × ${ctx.viewport.height}`} />
        ) : null}
        {ctx?.language ? <Row label="Language" value={ctx.language} /> : null}
        {location ? <Row label="Location" value={location} /> : null}
        {geo?.ip ? <Row label="IP" value={geo.ip} mono /> : null}
        {ctx?.origin ? (
          <Row label="Site" value={ctx.origin.replace(/^https?:\/\//, '')} />
        ) : null}
        {ctx?.pagePath && ctx.pagePath !== '/' ? (
          <Row label="Page" value={ctx.pagePath} mono />
        ) : null}
      </dl>
    </section>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className={styles.row}>
      <dt className={styles.label}>{label}</dt>
      <dd className={mono ? styles.valueMono : styles.value}>{value}</dd>
    </div>
  );
}

/** Tiny user-agent parser. Detects the common browser + OS families. Doesn't
 *  try to be exhaustive — falls back to undefined if it can't tell. */
function parseUA(ua?: string): { browser?: string; os?: string } {
  if (!ua) return {};
  // Browser detection — check Edge/Opera before Chrome (their UA strings
  // include "Chrome/..." too).
  let browser: string | undefined;
  const edge = ua.match(/Edg\/(\d+)/);
  const opera = ua.match(/OPR\/(\d+)/);
  const chrome = ua.match(/Chrome\/(\d+)/);
  const firefox = ua.match(/Firefox\/(\d+)/);
  const safariVer = ua.match(/Version\/(\d+)/);
  if (edge) browser = `Edge ${edge[1]}`;
  else if (opera) browser = `Opera ${opera[1]}`;
  else if (chrome) browser = `Chrome ${chrome[1]}`;
  else if (firefox) browser = `Firefox ${firefox[1]}`;
  else if (/Safari\//.test(ua) && safariVer) browser = `Safari ${safariVer[1]}`;

  // OS detection.
  let os: string | undefined;
  if (/Windows NT 10\.0/.test(ua)) os = 'Windows 10/11';
  else if (/Windows/.test(ua)) os = 'Windows';
  else {
    const mac = ua.match(/Mac OS X (\d+[._]\d+)/);
    const android = ua.match(/Android (\d+)/);
    if (mac && mac[1]) os = `macOS ${mac[1].replace('_', '.')}`;
    else if (android) os = `Android ${android[1]}`;
    else if (/iPhone|iPad/.test(ua)) os = 'iOS';
    else if (/Linux/.test(ua)) os = 'Linux';
  }

  const result: { browser?: string; os?: string } = {};
  if (browser) result.browser = browser;
  if (os) result.os = os;
  return result;
}
